import React, { useState, useEffect, useCallback } from "react";
import {
  Layout,
  Card,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Divider,
  Timeline,
  Avatar,
  Tooltip,
  Progress,
  Modal,
  Form,
  Input,
  Upload,
  Descriptions,
  Empty,
  Spin,
  Alert,
} from "antd";
import {
  FaTasks,
  FaArrowLeft,
  FaCheckCircle,
  FaPauseCircle,
  FaPlayCircle,
  FaClock,
  FaCalendarAlt,
  FaUsers,
  FaPlus,
  FaFile,
  FaExclamationCircle,
  FaHistory,
  FaUpload,
  FaHourglass,
  FaChartLine,
  FaFlagCheckered,
  FaBolt,
  FaSnowflake,
  FaUserShield,
  FaCog,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation } from "react-router-dom";
import Api from "../../api/Api";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import "../Tarefadetalhe.css";

dayjs.extend(duration);

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

/* ─────────────────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────────────────── */
const formatDateTime = (str) => {
  if (!str) return "—";
  try {
    return new Date(str).toLocaleString("pt-PT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return str;
  }
};

const formatMinutosParaHMS = (minutos) => {
  if (!minutos || minutos === 0) return "0h 0m";
  const absMinutos = Math.abs(minutos);
  const horas = Math.floor(absMinutos / 60);
  const mins = absMinutos % 60;
  const sinal = minutos < 0 ? "-" : "";
  return `${sinal}${horas}h ${mins}m`;
};

const getStatusInfo = (id_status) => {
  const map = {
    1: { label: "ABERTO", color: "blue", icon: <FaPlayCircle /> },
    2: { label: "FECHADO", color: "green", icon: <FaCheckCircle /> },
    3: { label: "SUSPENSO", color: "orange", icon: <FaPauseCircle /> },
    4: { label: "EM ANÁLISE", color: "purple", icon: <FaClock /> },
  };
  return map[id_status] || { label: "DESCONHECIDO", color: "default", icon: null };
};

/* ─────────────────────────────────────────────────────────────────────────────
   COMPONENTE: CARD DE MÉTRICAS DE TEMPO
───────────────────────────────────────────────────────────────────────────── */
const MetricasTempo = ({ relatorio }) => {
  if (!relatorio?.atraso) return null;

  const { atraso, minutos_suspensos, data_fecho } = relatorio;
  
  const corTipo = {
    atrasado: { 
      bg: "#fff1f0", 
      border: "#ffa39e", 
      text: "#cf1322", 
      icon: <FaExclamationCircle /> 
    },
    antecipado: { 
      bg: "#f6ffed", 
      border: "#b7eb8f", 
      text: "#389e0d", 
      icon: <FaBolt /> 
    },
    no_prazo: { 
      bg: "#e6fffb", 
      border: "#87e8de", 
      text: "#08979c", 
      icon: <FaFlagCheckered /> 
    },
  };

  const estilo = corTipo[atraso.status] || corTipo.no_prazo;

  return (
    <Card
      bordered={false}
      style={{
        borderRadius: 16,
        marginBottom: 24,
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        border: `1.5px solid ${estilo.border}`,
        background: estilo.bg,
      }}
      title={
        <Space>
          <FaChartLine style={{ color: "#1677ff" }} />
          <span style={{ fontWeight: 600 }}>Análise de Tempo</span>
        </Space>
      }
    >
      {/* Banner principal */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "14px 20px",
          borderRadius: 10,
          background: estilo.bg,
          border: `1px solid ${estilo.border}`,
          marginBottom: 20,
        }}
      >
        <span style={{ fontSize: 22, color: estilo.text }}>{estilo.icon}</span>
        <Text strong style={{ fontSize: 16, color: estilo.text }}>
          {atraso.formatado}
        </Text>
      </div>

      <Row gutter={[16, 16]}>
        {/* Status da Tarefa */}
        <Col xs={24} sm={12}>
          <Card
            size="small"
            style={{ borderRadius: 10, textAlign: "center", border: "1px solid #f0f0f0" }}
            bodyStyle={{ padding: "16px 12px" }}
          >
            <FaCalendarAlt style={{ color: "#1677ff", marginBottom: 6, fontSize: 18 }} />
            <div>
              <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
                Status
              </Text>
              <Text strong style={{ fontSize: 14, color: estilo.text }}>
                {atraso.status === "atrasado" 
                  ? "Atrasada" 
                  : atraso.status === "antecipado" 
                  ? "Antecipada" 
                  : "No Prazo"}
              </Text>
            </div>
          </Card>
        </Col>

        {/* Tempo de Atraso/Antecipação */}
        {(atraso.minutos_atraso > 0 || atraso.minutos_antecipacao > 0) && (
          <Col xs={24} sm={12}>
            <Card
              size="small"
              style={{
                borderRadius: 10,
                textAlign: "center",
                border: `1px solid ${estilo.border}`,
                background: estilo.bg,
              }}
              bodyStyle={{ padding: "16px 12px" }}
            >
              {atraso.status === "antecipado" ? (
                <FaBolt style={{ color: estilo.text, marginBottom: 6, fontSize: 18 }} />
              ) : (
                <FaSnowflake style={{ color: estilo.text, marginBottom: 6, fontSize: 18 }} />
              )}
              <div>
                <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
                  {atraso.status === "antecipado" ? "Antecipação" : "Atraso"}
                </Text>
                <Text strong style={{ fontSize: 14, color: estilo.text }}>
                  {atraso.status === "antecipado" 
                    ? formatMinutosParaHMS(atraso.minutos_antecipacao)
                    : formatMinutosParaHMS(atraso.minutos_atraso)}
                </Text>
              </div>
            </Card>
          </Col>
        )}

        {/* Tempo Suspenso */}
        {minutos_suspensos > 0 && (
          <Col xs={24} sm={12}>
            <Card
              size="small"
              style={{ 
                borderRadius: 10, 
                textAlign: "center", 
                border: "1px solid #ffe58f",
                background: "#fffbe6" 
              }}
              bodyStyle={{ padding: "16px 12px" }}
            >
              <FaPauseCircle style={{ color: "#faad14", marginBottom: 6, fontSize: 18 }} />
              <div>
                <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
                  Tempo Suspenso
                </Text>
                <Text strong style={{ fontSize: 14, color: "#faad14" }}>
                  {formatMinutosParaHMS(minutos_suspensos)}
                </Text>
              </div>
            </Card>
          </Col>
        )}

        {/* Status de Conclusão */}
        <Col xs={24} sm={12}>
          <Card
            size="small"
            style={{ borderRadius: 10, textAlign: "center", border: "1px solid #f0f0f0" }}
            bodyStyle={{ padding: "16px 12px" }}
          >
            {data_fecho ? (
              <>
                <FaCheckCircle style={{ color: "#52c41a", marginBottom: 6, fontSize: 18 }} />
                <div>
                  <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
                    Tarefa
                  </Text>
                  <Text strong style={{ fontSize: 14, color: "#52c41a" }}>
                    Concluída
                  </Text>
                </div>
              </>
            ) : (
              <>
                <FaHourglass style={{ color: "#faad14", marginBottom: 6, fontSize: 18 }} />
                <div>
                  <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
                    Tarefa
                  </Text>
                  <Text strong style={{ fontSize: 14, color: "#faad14" }}>
                    Em Andamento
                  </Text>
                </div>
              </>
            )}
          </Card>
        </Col>
      </Row>
    </Card>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   MODAL DE ADICIONAR ATUALIZAÇÃO
───────────────────────────────────────────────────────────────────────────── */
const AtualizacaoFormModal = ({ open, onClose, onSuccess, tarefaId, userId }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setFileList([]);
    }
  }, [open, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const formData = new FormData();
      formData.append("descricao", values.descricao || "");
      formData.append("id_user", userId);
      formData.append("id_tarefa", tarefaId);

      if (fileList.length > 0) {
        formData.append("file", fileList[0]);
      }

      await Api.AddAtualizacaoTarefa(formData);
      toast.success("Atualização adicionada com sucesso!");
      form.resetFields();
      setFileList([]);
      onSuccess();
      onClose();
    } catch (error) {
      if (error.errorFields) {
        toast.error("Preencha os campos obrigatórios.");
      } else {
        toast.error("Erro ao adicionar atualização.");
      }
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    onRemove: () => setFileList([]),
    beforeUpload: (file) => {
      setFileList([file]);
      return false;
    },
    fileList,
    maxCount: 1,
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={600}
      title={
        <Space>
          <FaPlus style={{ color: "#1677ff", fontSize: 20 }} />
          <span style={{ fontWeight: 600, fontSize: 18 }}>Nova Atualização</span>
        </Space>
      }
      footer={[
        <Button key="cancel" onClick={onClose} style={{ borderRadius: 6 }}>
          Cancelar
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={loading}
          style={{ borderRadius: 6, fontWeight: 500 }}
        >
          Adicionar
        </Button>,
      ]}
      bodyStyle={{ padding: "24px 24px 0" }}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label={<span style={{ fontWeight: 500 }}>Descrição</span>}
          name="descricao"
        >
          <TextArea rows={4} placeholder="Descreva a atualização da tarefa..." />
        </Form.Item>

        <Form.Item label={<span style={{ fontWeight: 500 }}>Anexar Arquivo (opcional)</span>}>
          <Upload {...uploadProps}>
            <Button icon={<FaUpload />} style={{ borderRadius: 6 }}>
              Selecionar Arquivo
            </Button>
          </Upload>
          <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: "block" }}>
            Máximo 10MB
          </Text>
        </Form.Item>
      </Form>
    </Modal>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   MODAL DE ALTERAR STATUS
   - Apenas diretores podem suspender tarefas
───────────────────────────────────────────────────────────────────────────── */
const AlterarStatusModal = ({ open, onClose, onSuccess, tarefaId, statusAtual, isDiretor }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [novoStatus, setNovoStatus] = useState(null);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setNovoStatus(null);
    }
  }, [open, form]);

  const getOpcoesStatus = () => {
    if (statusAtual === 1) {
      // ABERTO -> pode ir para SUSPENSO (só diretor) ou EM ANÁLISE
      const opcoes = [
        { id: 4, label: "EM ANÁLISE", color: "purple", icon: <FaClock />, requerMotivo: false },
      ];
      
      // Apenas diretor pode suspender
      if (isDiretor) {
        opcoes.unshift({
          id: 3,
          label: "SUSPENSO",
          color: "orange",
          icon: <FaPauseCircle />,
          requerMotivo: true,
          apenasDir: true,
        });
      }
      
      return opcoes;
    } else if (statusAtual === 3) {
      // SUSPENSO -> pode voltar para ABERTO (qualquer responsável)
      return [{ id: 1, label: "ABERTO", color: "blue", icon: <FaPlayCircle />, requerMotivo: false }];
    }
    return [];
  };

  const opcaoSelecionada = getOpcoesStatus().find((o) => o.id === novoStatus);
  const requerMotivo = opcaoSelecionada?.requerMotivo ?? false;

  const handleSubmit = async () => {
    if (!novoStatus) {
      toast.warning("Selecione um status!");
      return;
    }

    let descricao = "";

    if (requerMotivo) {
      try {
        const values = await form.validateFields();
        descricao = values.descricao;
      } catch {
        return;
      }
    }

    try {
      setLoading(true);
      await Api.AlterStatusTarefa({
        id: tarefaId,
        id_status: novoStatus,
        ...(requerMotivo && { descricao }),
      });
      toast.success("Status alterado com sucesso!");
      onSuccess(novoStatus);
      onClose();
    } catch {
      toast.error("Erro ao alterar status.");
    } finally {
      setLoading(false);
    }
  };

  const opcoes = getOpcoesStatus();

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={500}
      title={
        <Space>
          <FaExclamationCircle style={{ color: "#1677ff", fontSize: 20 }} />
          <span style={{ fontWeight: 600, fontSize: 18 }}>Alterar Status</span>
        </Space>
      }
      footer={[
        <Button key="cancel" onClick={onClose} style={{ borderRadius: 6 }}>
          Cancelar
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={loading}
          disabled={!novoStatus}
          style={{ borderRadius: 6, fontWeight: 500 }}
        >
          Confirmar
        </Button>,
      ]}
      bodyStyle={{ padding: "24px" }}
    >
      {opcoes.length === 0 ? (
        <Empty description="Nenhuma alteração de status disponível" />
      ) : (
        <div>
          <Text style={{ marginBottom: 16, display: "block" }}>
            Selecione o novo status:
          </Text>
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            {opcoes.map((opcao) => (
              <Card
                key={opcao.id}
                hoverable
                onClick={() => setNovoStatus(opcao.id)}
                style={{
                  borderRadius: 8,
                  border: novoStatus === opcao.id ? "2px solid #1677ff" : "1px solid #d9d9d9",
                  backgroundColor: novoStatus === opcao.id ? "#e6f4ff" : "#fff",
                  cursor: "pointer",
                }}
                bodyStyle={{ padding: "16px" }}
              >
                <Space>
                  <span style={{ fontSize: 22, color: opcao.color }}>{opcao.icon}</span>
                  <Text strong style={{ fontSize: 16 }}>
                    {opcao.label}
                  </Text>
                  {opcao.requerMotivo && (
                    <Tag color="warning" style={{ marginLeft: 8 }}>
                      Requer motivo
                    </Tag>
                  )}
                  {opcao.apenasDir && (
                    <Tag color="purple" icon={<FaUserShield />} style={{ marginLeft: 8 }}>
                      Apenas Diretor
                    </Tag>
                  )}
                </Space>
              </Card>
            ))}
          </Space>

          {/* Campo de motivo — aparece apenas quando suspenso é selecionado */}
          {requerMotivo && (
            <Form form={form} layout="vertical" style={{ marginTop: 20 }}>
              <Divider style={{ margin: "16px 0" }} />
              <Alert
                message="Permissão de Diretor"
                description="Apenas diretores podem suspender tarefas. Informe o motivo da suspensão."
                type="info"
                showIcon
                icon={<FaUserShield />}
                style={{ marginBottom: 16, borderRadius: 8 }}
              />
              <Form.Item
                label={<span style={{ fontWeight: 500 }}>Motivo da Suspensão</span>}
                name="descricao"
                rules={[{ required: true, message: "Informe o motivo da suspensão." }]}
              >
                <TextArea
                  rows={3}
                  placeholder="Descreva o motivo pelo qual a tarefa está sendo suspensa..."
                />
              </Form.Item>
            </Form>
          )}
        </div>
      )}
    </Modal>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   TAREFA DETALHE PAGE
───────────────────────────────────────────────────────────────────────────── */
const TarefaDetalhe = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tarefa: tarefaInicial } = location.state || {};

  const [relatorio, setRelatorio] = useState(null);
  const [atualizacoes, setAtualizacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [atualizacaoModalOpen, setAtualizacaoModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);

  const userStored = JSON.parse(localStorage.getItem("user")) || {};
  const isDiretor = userStored?.diretor === "S";

  useEffect(() => {
    document.title = `Tarefa: ${tarefaInicial?.titulo || "Detalhes"} | Dashboard`;
  }, [tarefaInicial]);

  const loadDados = useCallback(async () => {
    if (!tarefaInicial?.id) return;
    
    try {
      setLoading(true);
      
      // Carregar relatório da tarefa (novo endpoint)
      const respRelatorio = await Api.ListRelatorioTarefa({ id: tarefaInicial.id });
      setRelatorio(respRelatorio?.data || null);

      // Carregar atualizações
      const respAtualizacoes = await Api.ListAtualizacaoTarefa({ id: tarefaInicial.id });
      setAtualizacoes(Array.isArray(respAtualizacoes) ? respAtualizacoes : []);
    } catch {
      toast.error("Erro ao carregar dados da tarefa.");
    } finally {
      setLoading(false);
    }
  }, [tarefaInicial]);

  useEffect(() => {
    if (!tarefaInicial) {
      navigate("/projectos");
      return;
    }
    loadDados();
  }, [tarefaInicial, navigate, loadDados]);

  const isResponsavel = () => {
    if (!relatorio?.responsaveis) return false;
    return relatorio.responsaveis.some((r) => r.id === userStored.id);
  };

  const canAddUpdate = () => {
    return isResponsavel() && tarefaInicial?.id_status !== 3;
  };

  const canChangeStatus = () => {
    // Responsável pode alterar status (mas só diretor pode suspender)
    return isResponsavel() && (tarefaInicial?.id_status === 1 || tarefaInicial?.id_status === 3);
  };

  const handleAtualizacaoSuccess = () => loadDados();

  const handleStatusSuccess = (novoIdStatus) => {
    // Atualizar localmente sem reload
    if (tarefaInicial) {
      tarefaInicial.id_status = novoIdStatus;
    }
    loadDados();
  };

  if (!tarefaInicial || !relatorio) {
    return (
      <Layout style={{ background: "transparent", minHeight: "100vh" }}>
        <Content style={{ padding: "24px 32px", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Spin size="large" />
        </Content>
      </Layout>
    );
  }

  const statusInfo = getStatusInfo(tarefaInicial?.id_status);

  return (
    <Layout className="tarefa-detalhe-layout" style={{ background: "transparent" }}>
      <Content style={{ padding: "24px 32px" }}>
        <ToastContainer position="top-right" autoClose={4000} />

        {/* Botão voltar */}
        <Button
          icon={<FaArrowLeft />}
          onClick={() => navigate("/projectos")}
          style={{ marginBottom: 24, borderRadius: 6 }}
          size="large"
        >
          Voltar
        </Button>

        {/* Aviso quando tarefa está suspensa */}
        {tarefaInicial?.id_status === 3 && (
          <Alert
            message="Tarefa Suspensa"
            description="Esta tarefa está suspensa. Não é possível adicionar atualizações enquanto o status não for alterado para Aberto."
            type="warning"
            showIcon
            icon={<FaPauseCircle />}
            style={{ marginBottom: 24, borderRadius: 10 }}
          />
        )}

        {/* Cabeçalho da tarefa */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            marginBottom: 24,
            boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
          }}
          bodyStyle={{ padding: "32px" }}
        >
          <Row gutter={24} align="middle">
            <Col flex="auto">
              <Space>
                <Avatar
                  shape="square"
                  size={64}
                  style={{ backgroundColor: "#e6f4ff", color: "#1677ff", borderRadius: 12 }}
                  icon={<FaTasks />}
                />
                <div>
                  <Title level={2} style={{ margin: 0 }}>
                    {relatorio.titulo}
                  </Title>
                  <Text type="secondary" style={{ fontSize: 14 }}>
                    Etapa: {tarefaInicial.etapa || "—"}
                  </Text>
                </div>
              </Space>
            </Col>
            <Col>
              <Space direction="vertical" size="middle" align="end">
                <Tag
                  color={statusInfo.color}
                  style={{
                    borderRadius: 20,
                    padding: "8px 20px",
                    fontSize: 16,
                    fontWeight: 600,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {statusInfo.icon} {statusInfo.label}
                </Tag>
                <Space>
                  {canChangeStatus() && (
                    <Button
                      type="primary"
                      icon={<FaExclamationCircle />}
                      onClick={() => setStatusModalOpen(true)}
                      style={{ borderRadius: 6 }}
                    >
                      Alterar Status
                    </Button>
                  )}
                  {canAddUpdate() && (
                    <Button
                      type="primary"
                      icon={<FaPlus />}
                      onClick={() => setAtualizacaoModalOpen(true)}
                      style={{ borderRadius: 6 }}
                    >
                      Adicionar Atualização
                    </Button>
                  )}
                </Space>
              </Space>
            </Col>
          </Row>
        </Card>

        <Row gutter={24}>
          {/* Coluna esquerda */}
          <Col xs={24} lg={16}>
            {/* Descrição */}
            <Card
              bordered={false}
              style={{ borderRadius: 16, marginBottom: 24, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}
              title={
                <Space>
                  <FaTasks style={{ color: "#1677ff" }} />
                  <span style={{ fontWeight: 600 }}>Descrição</span>
                </Space>
              }
            >
              <Paragraph style={{ fontSize: 15, lineHeight: 1.8 }}>
                {relatorio.descricao || "Sem descrição"}
              </Paragraph>
            </Card>

            {/* Informações da tarefa */}
            <Card
              bordered={false}
              style={{ borderRadius: 16, marginBottom: 24, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}
              title={
                <Space>
                  <FaCalendarAlt style={{ color: "#1677ff" }} />
                  <span style={{ fontWeight: 600 }}>Informações</span>
                </Space>
              }
            >
              <Descriptions column={2} bordered>
                <Descriptions.Item label="Início">
                  {formatDateTime(relatorio.inicio)}
                </Descriptions.Item>
                <Descriptions.Item label="Fim Previsto">
                  {formatDateTime(relatorio.fim_previsto)}
                </Descriptions.Item>
                <Descriptions.Item label="Data de Fecho">
                  {relatorio.data_fecho ? formatDateTime(relatorio.data_fecho) : "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Percentagem">
                  <Progress
                    percent={parseFloat(relatorio.percentagem) || 0}
                    size="small"
                    status={parseFloat(relatorio.percentagem) === 100 ? "success" : "active"}
                  />
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Análise de Tempo */}
            <MetricasTempo relatorio={relatorio} />

            {/* Atualizações */}
            <Card
              bordered={false}
              style={{ borderRadius: 16, marginBottom: 24, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}
              title={
                <Space>
                  <FaHistory style={{ color: "#1677ff" }} />
                  <span style={{ fontWeight: 600 }}>Atualizações</span>
                </Space>
              }
            >
              {loading ? (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                  <Spin size="large" />
                </div>
              ) : atualizacoes.length === 0 ? (
                <Empty description="Nenhuma atualização ainda" />
              ) : (
                <Timeline
                  mode="left"
                  items={atualizacoes.map((atualizacao, index) => ({
                    color: "blue",
                    children: (
                      <Card
                        key={atualizacao.id}
                        size="small"
                        style={{
                          marginBottom: index < atualizacoes.length - 1 ? 16 : 0,
                          borderRadius: 8,
                        }}
                      >
                        <Space direction="vertical" style={{ width: "100%" }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {formatDateTime(atualizacao.created_at)}
                          </Text>
                          {atualizacao.descricao && (
                            <Paragraph style={{ margin: 0 }}>{atualizacao.descricao}</Paragraph>
                          )}
                          {atualizacao.file && (
                            <Button
                              type="link"
                              icon={<FaFile />}
                              href={atualizacao.file}
                              target="_blank"
                              style={{ padding: 0 }}
                            >
                              Ver Anexo
                            </Button>
                          )}
                        </Space>
                      </Card>
                    ),
                  }))}
                />
              )}
            </Card>
          </Col>

          {/* Coluna direita */}
          <Col xs={24} lg={8}>
            {/* Responsáveis */}
            <Card
              bordered={false}
              style={{ borderRadius: 16, marginBottom: 24, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}
              title={
                <Space>
                  <FaUsers style={{ color: "#1677ff" }} />
                  <span style={{ fontWeight: 600 }}>Responsáveis</span>
                </Space>
              }
            >
              {loading ? (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <Spin />
                </div>
              ) : !relatorio.responsaveis || relatorio.responsaveis.length === 0 ? (
                <Empty description="Sem responsáveis" />
              ) : (
                <Space direction="vertical" style={{ width: "100%" }} size="middle">
                  {relatorio.responsaveis.map((responsavel) => (
                    <Card
                      key={responsavel.id}
                      size="small"
                      style={{ borderRadius: 8 }}
                      bodyStyle={{ padding: "12px 16px" }}
                    >
                      <Space direction="vertical" style={{ width: "100%" }} size="small">
                        <Space>
                          <Avatar style={{ backgroundColor: "#1677ff" }} size="large">
                            {responsavel.nome.charAt(0).toUpperCase()}
                          </Avatar>
                          <div>
                            <Text strong>{responsavel.nome}</Text>
                            {responsavel.id === userStored.id && (
                              <div>
                                <Tag color="blue" style={{ marginTop: 4 }}>
                                  Você
                                </Tag>
                              </div>
                            )}
                          </div>
                        </Space>
                        
                        {/* Informações da escala */}
                        {responsavel.escala && (
                          <div style={{ 
                            marginTop: 8, 
                            padding: "8px 12px", 
                            background: "#f5f5f5", 
                            borderRadius: 6 
                          }}>
                            <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>
                              <FaCog style={{ marginRight: 4 }} />
                              {responsavel.escala.turno}
                            </Text>
                            <Space size="small" wrap>
                              <Tag style={{ fontSize: 10 }}>
                                Entrada: {responsavel.escala.entrada}
                              </Tag>
                              <Tag style={{ fontSize: 10 }}>
                                Saída: {responsavel.escala.saida}
                              </Tag>
                              <Tag style={{ fontSize: 10 }}>
                                Almoço: {responsavel.escala.almoco}
                              </Tag>
                            </Space>
                          </div>
                        )}
                      </Space>
                    </Card>
                  ))}
                </Space>
              )}
            </Card>

            {/* Períodos de Suspensão */}
            <Card
              bordered={false}
              style={{ borderRadius: 16, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}
              title={
                <Space>
                  <FaPauseCircle style={{ color: "#faad14" }} />
                  <span style={{ fontWeight: 600 }}>Períodos de Suspensão</span>
                </Space>
              }
            >
              {loading ? (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <Spin />
                </div>
              ) : !relatorio.suspensoes || relatorio.suspensoes.length === 0 ? (
                <Empty description="Nenhuma suspensão registrada" />
              ) : (
                <Space direction="vertical" style={{ width: "100%" }} size="middle">
                  {relatorio.suspensoes.map((suspensao, idx) => (
                    <Card
                      key={idx}
                      size="small"
                      style={{ 
                        borderRadius: 8, 
                        backgroundColor: suspensao.em_curso ? "#fff7e6" : "#fffbe6",
                        border: suspensao.em_curso ? "1px solid #ffa940" : "1px solid #ffe58f"
                      }}
                      bodyStyle={{ padding: "12px 16px" }}
                    >
                      <Space direction="vertical" size="small" style={{ width: "100%" }}>
                        {suspensao.em_curso && (
                          <Tag color="orange" style={{ marginBottom: 4 }}>
                            Em Curso
                          </Tag>
                        )}
                        
                        <div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Motivo:
                          </Text>
                          <div>
                            <Text strong>{suspensao.descricao || "—"}</Text>
                          </div>
                        </div>
                        
                        <div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Entrada:
                          </Text>
                          <div>
                            <Text strong>{formatDateTime(suspensao.entrada)}</Text>
                          </div>
                        </div>
                        
                        <div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Saída:
                          </Text>
                          <div>
                            <Text strong>
                              {suspensao.saida ? formatDateTime(suspensao.saida) : "Em suspensão"}
                            </Text>
                          </div>
                        </div>
                        
                        <Divider style={{ margin: "8px 0" }} />
                        
                        <div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Duração:
                          </Text>
                          <div>
                            <Text strong style={{ color: "#faad14" }}>
                              {formatMinutosParaHMS(suspensao.minutos)}
                            </Text>
                          </div>
                        </div>
                      </Space>
                    </Card>
                  ))}
                </Space>
              )}
            </Card>
          </Col>
        </Row>

        {/* Modais */}
        <AtualizacaoFormModal
          open={atualizacaoModalOpen}
          onClose={() => setAtualizacaoModalOpen(false)}
          onSuccess={handleAtualizacaoSuccess}
          tarefaId={tarefaInicial.id}
          userId={userStored.id}
        />

        <AlterarStatusModal
          open={statusModalOpen}
          onClose={() => setStatusModalOpen(false)}
          onSuccess={handleStatusSuccess}
          tarefaId={tarefaInicial.id}
          statusAtual={tarefaInicial.id_status}
          isDiretor={isDiretor}
        />
      </Content>
    </Layout>
  );
};

export default TarefaDetalhe;